/**
 * 提供商密钥存储（T15）。
 *
 * 契约要求 `apiKey` **绝不写进** `ProviderConfig`（`src/core/types.ts` 里只有
 * `apiKeyRef`）。因此这里提供一层极薄的本地密钥存储，把明文密钥与配置里的
 * `apiKeyRef` 解耦：
 *
 * - 键名统一带 `zxtalk.secret.` 前缀，便于导出 / 清理时识别；
 * - 底层用 localStorage（沿用 T08 `core/settings.ts` 同款「存储不可用就降级」策略）；
 * - 只做读写删，不含任何业务判断。
 *
 * 安卓移植：应替换为 EncryptedSharedPreferences / Keystore，接口保持不变。
 */

export const SECRET_KEY_PREFIX = 'zxtalk.secret.';

function storage(): Storage | undefined {
  try {
    return typeof globalThis.localStorage === 'undefined' ? undefined : globalThis.localStorage;
  } catch {
    return undefined;
  }
}

/** 由提供商 id 生成稳定的密钥引用（同时也是 localStorage 的完整键名）。 */
export function secretRefForId(id: string): string {
  const trimmed = id.trim();
  if (trimmed === '') throw new Error('[secret-store] 提供商 id 不能为空');
  return `${SECRET_KEY_PREFIX}${trimmed}`;
}

/** 归一化任意引用为带前缀的完整键名（已带前缀则不重复叠加）。 */
export function secretStorageKey(ref: string): string {
  const trimmed = ref.trim();
  if (trimmed === '') throw new Error('[secret-store] 密钥引用不能为空');
  return trimmed.startsWith(SECRET_KEY_PREFIX) ? trimmed : `${SECRET_KEY_PREFIX}${trimmed}`;
}

/** 读取密钥；不存在或存储不可用时返回 null。 */
export function readSecret(ref: string): string | null {
  const store = storage();
  if (!store) return null;
  return store.getItem(secretStorageKey(ref));
}

/** 写入密钥；空字符串视为删除（避免留下空值残留）。 */
export function writeSecret(ref: string, value: string): void {
  if (value === '') {
    removeSecret(ref);
    return;
  }
  storage()?.setItem(secretStorageKey(ref), value);
}

/** 删除密钥；不存在时静默通过。 */
export function removeSecret(ref: string): void {
  storage()?.removeItem(secretStorageKey(ref));
}

/** 密钥是否存在（列表页展示「已配置密钥 / 未配置」）。 */
export function hasSecret(ref: string): boolean {
  return readSecret(ref) !== null;
}

/** 列出本模块管理的全部密钥键名（导出 / 清理用）。 */
export function listSecretKeys(): string[] {
  const store = storage();
  if (!store) return [];
  const keys: string[] = [];
  for (let index = 0; index < store.length; index += 1) {
    const key = store.key(index);
    if (key?.startsWith(SECRET_KEY_PREFIX)) keys.push(key);
  }
  return keys;
}